import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Button,
  Form,
  Row,
  Col,
  Alert,
  Spinner,
} from 'react-bootstrap';
import { FileTextOutlined, DownloadOutlined } from '@ant-design/icons';
import { invoicesAPI } from '../../services/apiService';
import { translateError } from '../../utils/errorTranslator';
import { generationsLabel } from '../../utils/pluralize';
import {
  maskDigits,
  formatPhone,
  validateInvoiceForm,
  buildInvoicePayload,
  parseInvoiceApiError,
} from '../../utils/invoiceFormUtils';
import StackedModal from '../common/StackedModal';

const Modal = StackedModal;

const EMPTY_FORM = {
  tariffId: '',
  inn: '',
  kpp: '',
  ogrn: '',
  organizationName: '',
  directorName: '',
  legalAddress: '',
  bankAccount: '',
  bankName: '',
  corrAccount: '',
  bik: '',
  phone: '',
  email: '',
};

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

const InvoicePaymentModal = ({ show, onHide, tariffs, user }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState({});
  const [loadingInn, setLoadingInn] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [dadataConfigured, setDadataConfigured] = useState(null);
  const innTimerRef = useRef(null);

  const selectedTariff = useMemo(
    () => tariffs.find((t) => t.id === form.tariffId) || null,
    [tariffs, form.tariffId]
  );

  useEffect(() => {
    if (!show) return;
    setForm({
      ...EMPTY_FORM,
      email: user?.email || '',
      phone: user?.phone ? formatPhone(user.phone) : '',
      tariffId: tariffs[0]?.id || '',
    });
    setFieldErrors({});
    setError('');
    setSuccessMessage('');
    setDadataConfigured(null);
  }, [show, user, tariffs]);

  const clearFieldError = (field) => {
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    clearFieldError(field);
    setError('');
    setSuccessMessage('');
  };

  const updateDigitsField = (field, value, maxLength) => {
    updateField(field, maskDigits(value, maxLength));
  };

  const applyCompanyData = (company) => {
    if (!company) return;
    setForm((prev) => ({
      ...prev,
      kpp: company.kpp ? maskDigits(company.kpp, 9) : prev.kpp,
      ogrn: company.ogrn ? maskDigits(company.ogrn, 15) : prev.ogrn,
      organizationName: company.organizationName || prev.organizationName,
      directorName: company.directorName || prev.directorName,
      legalAddress: company.legalAddress || prev.legalAddress,
      phone: company.phone ? formatPhone(company.phone) : prev.phone,
      email: company.email || prev.email,
    }));
    ['kpp', 'ogrn', 'organizationName', 'directorName', 'legalAddress', 'phone', 'email'].forEach(clearFieldError);
  };

  const lookupInn = async (inn) => {
    const digits = maskDigits(inn, 12);
    if (digits.length !== 10 && digits.length !== 12) return;

    try {
      setLoadingInn(true);
      const result = await invoicesAPI.getCompanyByInn(digits);
      setDadataConfigured(result.configured);
      if (result.company) {
        applyCompanyData(result.company);
      }
    } catch (err) {
      console.error('INN lookup error:', err);
    } finally {
      setLoadingInn(false);
    }
  };

  const handleInnChange = (value) => {
    const digits = maskDigits(value, 12);
    updateField('inn', digits);
    if (innTimerRef.current) {
      clearTimeout(innTimerRef.current);
    }
    innTimerRef.current = setTimeout(() => {
      lookupInn(digits);
    }, 500);
  };

  useEffect(() => () => {
    if (innTimerRef.current) clearTimeout(innTimerRef.current);
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccessMessage('');

    const validation = validateInvoiceForm(form);
    if (!validation.isValid) {
      setFieldErrors(validation.fieldErrors);
      setError('Исправьте ошибки в форме перед формированием счёта.');
      return;
    }

    setFieldErrors({});
    setCreating(true);

    try {
      const payload = buildInvoicePayload(form);
      const response = await invoicesAPI.createInvoice(payload);
      const contentType = response.headers['content-type'] || '';

      if (!contentType.includes('application/pdf')) {
        throw new Error('Неверный формат ответа сервера');
      }

      const rawInvoiceNumber = response.headers['x-invoice-number'] || 'invoice';
      const invoiceNumber = rawInvoiceNumber === 'invoice'
        ? rawInvoiceNumber
        : decodeURIComponent(rawInvoiceNumber);
      downloadBlob(response.data, `${invoiceNumber}.pdf`);
      setSuccessMessage(`Счёт № ${invoiceNumber} сформирован и загружен на ваш компьютер.`);
    } catch (err) {
      const parsed = await parseInvoiceApiError(err);
      setFieldErrors(parsed.fieldErrors || {});
      setError(translateError(parsed.message, 'Не удалось сформировать счёт'));
    } finally {
      setCreating(false);
    }
  };

  const formatPrice = (price) => {
    const num = parseFloat(price);
    if (Number.isNaN(num)) return '—';
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(num);
  };

  const renderFieldError = (field) => {
    if (!fieldErrors[field]) return null;
    return (
      <Form.Control.Feedback type="invalid" className="d-block">
        {fieldErrors[field]}
      </Form.Control.Feedback>
    );
  };

  return (
    <Modal
      show={show}
      onHide={creating ? undefined : onHide}
      size="lg"
      centered
      backdrop="static"
      keyboard={!creating}
      className="invoice-modal"
    >
      <Modal.Header closeButton={!creating}>
        <Modal.Title>
          <FileTextOutlined className="me-2" />
          Выставить счёт
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body className="invoice-modal__body">
          {creating && (
            <div className="invoice-modal__loading-overlay" aria-live="polite">
              <Spinner animation="border" variant="primary" />
              <p className="invoice-modal__loading-title">Формируем счёт…</p>
              <p className="invoice-modal__loading-text">
                Готовим PDF-документ. Обычно это занимает несколько секунд.
              </p>
            </div>
          )}

          {error && <Alert variant="danger">{error}</Alert>}
          {successMessage && <Alert variant="success">{successMessage}</Alert>}

          <p className="text-muted small">
            Заполните реквизиты организации. Счёт будет сформирован в PDF и сразу загружен на ваш компьютер.
            Ничего не отправляется автоматически.
          </p>

          <fieldset disabled={creating} className="invoice-modal__fieldset">
            <Form.Group className="mb-3">
              <Form.Label>Тариф *</Form.Label>
              <Form.Select
                value={form.tariffId}
                onChange={(e) => updateField('tariffId', e.target.value)}
                isInvalid={!!fieldErrors.tariffId}
                required
              >
                <option value="">Выберите тариф</option>
                {tariffs.map((tariff) => (
                  <option key={tariff.id} value={tariff.id}>
                    {tariff.name} — {formatPrice(tariff.price)} / {generationsLabel(tariff.requestsCount)}
                  </option>
                ))}
              </Form.Select>
              {renderFieldError('tariffId')}
              {selectedTariff && (
                <Form.Text className="text-muted">
                  {selectedTariff.name}: {generationsLabel(selectedTariff.requestsCount)}, сумма {formatPrice(selectedTariff.price)}
                </Form.Text>
              )}
            </Form.Group>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>ИНН *</Form.Label>
                  <Form.Control
                    value={form.inn}
                    onChange={(e) => handleInnChange(e.target.value)}
                    placeholder="10 или 12 цифр"
                    inputMode="numeric"
                    maxLength={12}
                    isInvalid={!!fieldErrors.inn}
                    required
                  />
                  {renderFieldError('inn')}
                  {loadingInn && (
                    <Form.Text className="text-muted">
                      <Spinner animation="border" size="sm" className="me-1" />
                      Поиск организации...
                    </Form.Text>
                  )}
                  {dadataConfigured === false && (
                    <Form.Text className="text-muted">
                      Автозаполнение не подключено — заполните поля вручную.
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>КПП</Form.Label>
                  <Form.Control
                    value={form.kpp}
                    onChange={(e) => updateDigitsField('kpp', e.target.value, 9)}
                    placeholder="9 цифр"
                    inputMode="numeric"
                    maxLength={9}
                    isInvalid={!!fieldErrors.kpp}
                  />
                  {renderFieldError('kpp')}
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>ОГРН</Form.Label>
                  <Form.Control
                    value={form.ogrn}
                    onChange={(e) => updateDigitsField('ogrn', e.target.value, 15)}
                    placeholder="13 или 15 цифр"
                    inputMode="numeric"
                    maxLength={15}
                    isInvalid={!!fieldErrors.ogrn}
                  />
                  {renderFieldError('ogrn')}
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Полное наименование организации *</Form.Label>
              <Form.Control
                value={form.organizationName}
                onChange={(e) => updateField('organizationName', e.target.value)}
                isInvalid={!!fieldErrors.organizationName}
                required
              />
              {renderFieldError('organizationName')}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>ФИО директора *</Form.Label>
              <Form.Control
                value={form.directorName}
                onChange={(e) => updateField('directorName', e.target.value)}
                isInvalid={!!fieldErrors.directorName}
                required
              />
              {renderFieldError('directorName')}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Юридический адрес *</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={form.legalAddress}
                onChange={(e) => updateField('legalAddress', e.target.value)}
                isInvalid={!!fieldErrors.legalAddress}
                required
              />
              {renderFieldError('legalAddress')}
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Расчётный счёт *</Form.Label>
                  <Form.Control
                    value={form.bankAccount}
                    onChange={(e) => updateDigitsField('bankAccount', e.target.value, 20)}
                    placeholder="20 цифр"
                    inputMode="numeric"
                    maxLength={20}
                    isInvalid={!!fieldErrors.bankAccount}
                    required
                  />
                  {renderFieldError('bankAccount')}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Банк *</Form.Label>
                  <Form.Control
                    value={form.bankName}
                    onChange={(e) => updateField('bankName', e.target.value)}
                    isInvalid={!!fieldErrors.bankName}
                    required
                  />
                  {renderFieldError('bankName')}
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Корр. счёт *</Form.Label>
                  <Form.Control
                    value={form.corrAccount}
                    onChange={(e) => updateDigitsField('corrAccount', e.target.value, 20)}
                    placeholder="20 цифр"
                    inputMode="numeric"
                    maxLength={20}
                    isInvalid={!!fieldErrors.corrAccount}
                    required
                  />
                  {renderFieldError('corrAccount')}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>БИК *</Form.Label>
                  <Form.Control
                    value={form.bik}
                    onChange={(e) => updateDigitsField('bik', e.target.value, 9)}
                    placeholder="9 цифр"
                    inputMode="numeric"
                    maxLength={9}
                    isInvalid={!!fieldErrors.bik}
                    required
                  />
                  {renderFieldError('bik')}
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Телефон *</Form.Label>
                  <Form.Control
                    type="tel"
                    value={form.phone}
                    onChange={(e) => updateField('phone', formatPhone(e.target.value))}
                    placeholder="+7 (XXX) XXX-XX-XX"
                    maxLength={18}
                    isInvalid={!!fieldErrors.phone}
                    required
                  />
                  {renderFieldError('phone')}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email *</Form.Label>
                  <Form.Control
                    type="email"
                    value={form.email}
                    onChange={(e) => updateField('email', e.target.value.replace(/\s/g, ''))}
                    placeholder="email@company.ru"
                    isInvalid={!!fieldErrors.email}
                    required
                  />
                  {renderFieldError('email')}
                </Form.Group>
              </Col>
            </Row>
          </fieldset>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={creating}>
            Закрыть
          </Button>
          <Button variant="primary" type="submit" disabled={creating || !form.tariffId}>
            {creating ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Формируем документ...
              </>
            ) : (
              <>
                <DownloadOutlined className="me-2" />
                Скачать счёт
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default InvoicePaymentModal;
